import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { changePassword, logout } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

/**
 * Change-password screen for the POS. Reached two ways: forced (an admin
 * generated a temp password, so `RequirePasswordChange` pins the staffer here
 * until they rotate it) or voluntary (the "Change password" header link). On
 * success the store flag clears and we return to the till.
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const setMustChangePassword = useAuthStore((s) => s.setMustChangePassword);
  const forced = useAuthStore((s) => s.user?.mustChangePassword ?? false);
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Choose a new password different from the current one.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setMustChangePassword(false);
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
      setError(
        res?.status === 429
          ? 'Too many attempts. Please wait an hour before trying again.'
          : res?.data?.error === 'invalid_current_password'
            ? 'The current password is incorrect.'
            : 'Could not change password. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{forced ? 'Set a new password' : 'Change password'}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {forced
              ? 'An admin reset your password. Pick a new one to continue.'
              : 'Enter your current password and choose a new one.'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="cur">
                {forced ? 'Current (temp) password' : 'Current password'}
              </label>
              <Input
                id="cur"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="new">New password</label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNew(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="confirm">Confirm new password</label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (forced) {
                    void logout().then(() => navigate('/login', { replace: true }));
                  } else {
                    navigate('/', { replace: true });
                  }
                }}
                disabled={submitting}
                className="flex-1"
              >
                {forced ? 'Sign out' : 'Cancel'}
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Saving…' : 'Save password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
